import { safe } from '../../documentos/commercialExcelUtils';
import { htPdfDrawFullWidthRowFrame } from './htPdfFullWidthFrame';

export const PAD = 1.2;
export const FONT_SM = 6.2;
export const EXCEL_PT_TO_MM = 25.4 / 72;

const DEFAULT_ROW_PT = 21.45;

export function ptToMm(pt) {
  return pt * EXCEL_PT_TO_MM;
}

export function rowHeightMm(excelRow, heightPtByRow = {}) {
  const pt = heightPtByRow[excelRow] ?? DEFAULT_ROW_PT;
  return ptToMm(pt);
}

/** Ancho texto de borde a borde del marco B–F (líneas rojas de referencia). */
export function contentBand(xs) {
  const x = xs[0];
  const w = xs[5] - x;
  return { x, w, half: w / 2 };
}

/** Columnas C | D | E (plantilla Invoice 5019), sin trazos verticales internos. */
export function gridColumns(xs) {
  return {
    xC: xs[1],
    wC: xs[2] - xs[1],
    xD: xs[2],
    wD: xs[3] - xs[2],
    xE: xs[3],
    wE: xs[5] - xs[3],
  };
}

/** Fila tipo grilla: etiqueta | valor | columna derecha. */
export function drawGridRow(doc, xs, y, excelRow, textC, textD, textE, heightPtByRow = {}) {
  const h = rowHeightMm(excelRow, heightPtByRow);
  htPdfDrawFullWidthRowFrame(doc, xs, y, h, excelRow, []);
  const { xC, wC, xD, wD, xE, wE } = gridColumns(xs);
  drawTextInBox(doc, textC, xC, y, wC, h);
  drawTextInBox(doc, textD, xD, y, wD, h);
  drawTextInBox(doc, textE, xE, y, wE, h);
  return y + h;
}

export function joinLabelValue(label, value) {
  const l = safe(label).trim();
  const v = safe(value).trim();
  return v ? `${l} ${v}` : l;
}

export function drawTextInBox(doc, text, x, y, w, h, opts = {}) {
  const pad = opts.pad ?? PAD;
  doc.setFont('helvetica', opts.italic ? 'italic' : 'normal');
  doc.setFontSize(FONT_SM);
  doc.text(safe(text), x + pad, y + h / 2, {
    baseline: 'middle',
    maxWidth: Math.max(1, w - pad * 2),
  });
}

/** Mitad izq. = columnas C+D; mitad der. = E+F (proporciones plantilla). */
export function contentMainSides(xs) {
  const xLeft = xs[0];
  const xSplit = xs[3];
  const xEnd = xs[5];
  return {
    xLeft,
    wLeft: xSplit - xLeft,
    xRight: xSplit,
    wRight: xEnd - xSplit,
  };
}

/** Dos bloques que ocupan todo el ancho útil (sin hueco central). */
export function drawMainSidesRow(
  doc,
  xs,
  y,
  excelRow,
  leftText,
  rightText,
  heightPtByRow = {},
  opts = {}
) {
  const h = rowHeightMm(excelRow, heightPtByRow);
  htPdfDrawFullWidthRowFrame(doc, xs, y, h, excelRow, []);
  const { xLeft, wLeft, xRight, wRight } = contentMainSides(xs);
  const pad = opts.pad ?? PAD;
  drawTextInBox(doc, leftText, xLeft, y, wLeft, h, { pad });
  drawTextInBox(doc, rightText, xRight, y, wRight, h, { pad });
  return y + h;
}

/** Fila centrada: prefijo en negrita + resto normal. */
export function drawCenteredBoldPrefixRow(
  doc,
  xs,
  y,
  excelRow,
  boldPrefix,
  rest,
  heightPtByRow = {},
  opts = {}
) {
  const h = rowHeightMm(excelRow, heightPtByRow);
  htPdfDrawFullWidthRowFrame(doc, xs, y, h, excelRow, []);
  const { x, w } = contentBand(xs);
  const midY = y + h / 2;
  const pad = opts.pad ?? PAD;

  doc.setFontSize(FONT_SM);
  doc.setFont('helvetica', 'bold');
  const wBold = doc.getTextWidth(safe(boldPrefix));
  doc.setFont('helvetica', 'normal');
  const wRest = doc.getTextWidth(safe(rest));
  const total = wBold + wRest;
  let startX = x + pad + (w - pad * 2 - total) / 2;
  if (startX < x + pad) startX = x + pad;

  doc.setFont('helvetica', 'bold');
  doc.text(safe(boldPrefix), startX, midY, { baseline: 'middle' });
  doc.setFont('helvetica', 'normal');
  doc.text(safe(rest), startX + wBold, midY, { baseline: 'middle' });
  return y + h;
}

/** Fila sin líneas verticales internas (solo marco B–F según plantilla). */
export function drawFullWidthRow(doc, xs, y, excelRow, text, heightPtByRow = {}, opts = {}) {
  const h = rowHeightMm(excelRow, heightPtByRow);
  htPdfDrawFullWidthRowFrame(doc, xs, y, h, excelRow, []);
  const { x, w } = contentBand(xs);
  drawTextInBox(doc, text, x, y, w, h, opts);
  return y + h;
}

/** Dos mitades de texto sin línea vertical central. */
export function drawLeftRightRow(
  doc,
  xs,
  y,
  excelRow,
  leftText,
  rightText,
  heightPtByRow = {}
) {
  const h = rowHeightMm(excelRow, heightPtByRow);
  htPdfDrawFullWidthRowFrame(doc, xs, y, h, excelRow, []);
  const { x, w, half } = contentBand(xs);
  const gap = 1.2;
  drawTextInBox(doc, leftText, x, y, half - gap, h);
  drawTextInBox(doc, rightText, x + half + gap, y, half - gap, h);
  return y + h;
}
